import React, { useState, useEffect } from 'react';
import { Box, Button, CircularProgress, Divider, Paper, Tooltip, Typography } from '@mui/material';
import BasicDetsCard from './BasicDetsCard';
import DateLocationCard from './DateLocationCard';
import FinalDetsCard from './FinalDetsCard';
import MsgModalBox from '../../../container/MessageModalBox/MsgModalBox';
import axios from 'axios';
import moment from 'moment';
import { APIURL } from '../../../config';
import { Redirect } from 'react-router-dom';
import CircleIcon from '@mui/icons-material/Circle';
import AssignmentOutlinedIcon from '@mui/icons-material/AssignmentOutlined';
import EventOutlinedIcon from '@mui/icons-material/EventOutlined';
import TuneOutlinedIcon from '@mui/icons-material/TuneOutlined';
import { convertTime } from 'src/utils/time';

const DATETIME_FORMAT = 'YYYY-MM-DDTHH:mm:ss';
const DEFAULT_LAT = 40.730610;
const DEFAULT_LON = -73.935242;

const FINAL_DETS_ACTIONS = {
    radioLow: { tPriority: 'Low' },
    radioMed: { tPriority: 'Med' },
    radioHigh: { tPriority: 'High' },
    radioBillYes: { tIsBillable: true },
    radioBillNo: { tIsBillable: false },
    radioSigYes: { tSigReq: 1 },
    radioSigNo: { tSigReq: 0 },
};

const parseTravelTimeMinutes = (value) => {
    if (!value) return null;
    const parts = value.split(':');
    if (parts.length !== 2) return null;
    const hours = parseInt(parts[0], 10);
    const minutes = parseInt(parts[1], 10);
    return isNaN(hours) || isNaN(minutes) ? null : hours * 60 + minutes;
};

const SectionCard = ({ icon, title, children }) => (
    <Paper
        elevation={1}
        sx={{
            flex: '1 1 0',
            minWidth: 280,
            p: 2,
            borderRadius: 2,
            display: 'flex',
            flexDirection: 'column',
            bgcolor: '#ffffff',
            border: '1px solid #e0e7ef',
        }}
    >
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
            {icon}
            <Typography variant="subtitle1" sx={{ ml: 1, fontWeight: 600, color: '#2c3e50' }}>
                {title}
            </Typography>
        </Box>
        <Divider sx={{ mb: 1.5 }} />
        <Box sx={{ flex: 1 }}>{children}</Box>
    </Paper>
);

const TaskForm_v2 = (props) => {
    const companyAddress = (props.locList || []).filter((loc) => loc && loc.location_type === 'Company');
    const com_lat = companyAddress.length === 0 ? DEFAULT_LAT : Number(companyAddress[0].location_lat);
    const com_lon = companyAddress.length === 0 ? DEFAULT_LON : Number(companyAddress[0].location_lon);

    const findSelectedProject = (projectId, currentBasicDetails = basicDetails) => {
        const list = props.edit ? (props.projList || []) : projectList;
        const id = projectId !== undefined ? projectId : currentBasicDetails.tProject;
        return list.find((proj) => proj && proj.pid === +id);
    };

    const [goHome, setGoHome] = useState(false);
    const [projectList, setProjectList] = useState([]);
    const [basicDetails, setBasicDetails] = useState({
        tTitle: props.edit ? props.formVals.tas_title : "",
        tDesc: props.edit ? props.formVals.tas_description : "",
        tProject: "",
        tPreReq: props.edit ? (!!props.formVals.tas_prequisites_task ? props.formVals.tas_prequisites_task : "No Pre Req") : "No Pre Req",
        tType: props.edit ? props.formVals.tas_type : "",
    });
    const [dateLocation, setDateLocation] = useState({
        tStrtDate: props.edit ? moment(props.formVals.tas_start_time).format(DATETIME_FORMAT) : moment().format(DATETIME_FORMAT),
        tEndDate: props.edit ? moment(props.formVals.tas_end_time).format(DATETIME_FORMAT) : moment().add(1, 'm').format(DATETIME_FORMAT),
        tStartTime: props.edit ? moment(props.formVals.tas_start_time).format(DATETIME_FORMAT) : moment().format(DATETIME_FORMAT),
        tEndTime: props.edit ? moment(props.formVals.tas_end_time).format(DATETIME_FORMAT) : moment().add(1, 'm').format(DATETIME_FORMAT),
        tLoc: props.edit ? props.task_location?.map((loc) => { if (loc) { return { value: loc?.loc_id, title: loc?.loc_addr_line1 + " " + loc?.loc_addr_line2 + ", " + loc?.loc_city + ", " + loc?.loc_state + ", " + loc?.loc_zip } } }) : [],
    });
    const [recurrenceDetails, setRecurrenceDetails] = useState(null);
    const [finalDetails, setFinalDetails] = useState({
        tAssTo: props.edit ? props.task_emp : "",
        tPriority: props.edit ? props.taskPriority : "Med",
        tIsBillable: props.edit ? props.taskIsBillable : true,
        tSigReq: props.edit ? (props.tSigReq === undefined ? 0 : props.tSigReq) : 0,
        tComment: props.edit ? props.taskComment : "",
        tContacts: props.edit ? (props.taskContacts?.map((contact) => ({ id: contact.con_id, label: contact.con_first_name + " " + contact.con_last_name }))) : [],
        tTravelTime: "",
        tExpenses: "",
    });
    const [task_emp_timezone, setTask_emp_timezone] = useState('');
    const [officeLocList, setOfficeLocList] = useState([]);
    const [tOloc, setTOloc] = useState(props.edit ? (props.formVals.tas_oloc_id ?? null) : null);
    const [mapPushPins, setMapPushPins] = useState([]);
    const [taskDates, setTaskDates] = useState([]);
    const [showModal, setShowModal] = useState(false);
    const [addError, setAddError] = useState(false);
    const [showAlert, setShowAlert] = useState(false);
    const [showNoEmployeeAlert, setShowNoEmployeeAlert] = useState(false);
    const [errorMsg, setErrorMsg] = useState('');
    const [loc_comp_pos] = useState([com_lat, com_lon]);
    const [locList, setLocList] = useState([]);
    const [taskAuthorizationStatus, setTaskAuthorizationStatus] = useState(null);
    const [taskAuthorizationText, setTaskAuthorizationText] = useState('');
    const [isServiceProvider] = useState(Array.isArray(props.user?.comp_settings) && props.user.comp_settings.find(setting => setting.feature_id === 102 && setting.feature_value === 'primaryBusinessType') ? true : false);
    const [isSupport] = useState(Array.isArray(props.user?.comp_settings) && props.user.comp_settings.find(setting => setting.feature_id === 101 && setting.feature_value === 'primaryBusinessType') ? true : false);
    const [empTasksAvailable, setEmpTasksAvailable] = useState(false);
    const [typeList, setTypeList] = useState([]);
    const [cust_id, setCust_id] = useState(null);
    const [contactList, setContactList] = useState([]);
    const [taskList, setTaskList] = useState([]);
    const [isCreatingTask, setIsCreatingTask] = useState(false);
    const [customerPreferences, setCustomerPreferences] = useState([]);
    const [submitAttempted, setSubmitAttempted] = useState(false);

    useEffect(() => {
        const fetchProjects = async () => {
            if (!props.edit) {
                const response = await axios.get(APIURL + "/project/associated", {
                    params: { company_id: props.comp_id, employee_id: props.user.emp_id },
                });
                setProjectList(response.data);
            }
        };
        fetchProjects();
    }, [props.edit, props.comp_id]);

    useEffect(() => {
        const fetchOfficeLocations = async () => {
            if (!props.comp_id) return;
            try {
                const response = await axios.get(APIURL + "/company/officeLocation", {
                    params: { company_id: props.comp_id },
                });
                const options = (Array.isArray(response.data) ? response.data : []).map((r) => ({
                    value: r.oloc_id,
                    title: [r.oloc_addr_line1, r.oloc_addr_line2, r.oloc_city, r.oloc_state, r.oloc_zip].filter(Boolean).join(', '),
                }));
                setOfficeLocList(options);
            } catch (err) {
                console.log(err);
            }
        };
        fetchOfficeLocations();
    }, [props.comp_id]);

    useEffect(() => {
        const fetchTaskList = async () => {
            const taskListResponse = await axios.get(APIURL + "/dropdowns/dropdownType/taskslist", {
                params: { comp_id: props.comp_id, projID: basicDetails.tProject },
            });
            setTaskList(taskListResponse.data);
        };
        if (basicDetails.tProject) fetchTaskList();
    }, [basicDetails.tProject, props.comp_id]);

    useEffect(() => {
        const updateTaskAuthStatus = async () => {
            if (isServiceProvider) {
                let remainingUnits = await setTaskAuthStatus();
                setTaskAuthorizationStatus(!!remainingUnits ? remainingUnits : null);
                setTaskAuthorizationText(!!remainingUnits
                    ? remainingUnits > 0 ? "Under prior authorization limit" : "Exceeds prior authorization limit"
                    : ""
                );
            }
        };
        updateTaskAuthStatus();
    }, [basicDetails.tProject, basicDetails.tType, dateLocation.tStartTime, dateLocation.tEndTime, isServiceProvider]);

    useEffect(() => {
        const fetchTaskTypes = async () => {
            if (!empTasksAvailable && cust_id) {
                let custTasks = await axios.get(APIURL + "/customers/CustomerTasks", { params: { comp_id: props.comp_id, customer_id: cust_id } });
                let cusTaskTypes = [];
                if (custTasks.data.length !== 0) {
                    custTasks.data.forEach((task) => cusTaskTypes.push(task.EntityValue));
                    setTypeList(cusTaskTypes);
                    setBasicDetails(prev => ({ ...prev, tType: null }));
                } else {
                    let taskTypes = await axios.get(APIURL + '/dropdowns/dropdownType/taskType', { params: { comp_id: props.comp_id, employeeType: props.user.type } });
                    setTypeList(taskTypes.data);
                    setBasicDetails(prev => ({ ...prev, tType: null }));
                }
            }
        };
        fetchTaskTypes();
    }, [cust_id, empTasksAvailable, props.comp_id, props.user.type]);

    useEffect(() => {
        const initialize = async () => {
            axios.defaults.headers = {
                'Content-Type': 'application/json',
                'Access-Control-Allow-Origin': '*',
                "Access-Control-Allow-Headers": '*',
                "Access-Control-Allow-Methods": "PUT, GET, POST, DELETE, OPTIONS",
                "ACCESS-CONTROL-ALLOW-CREDENTIAL": true,
                Authorization: 'Bearer ' + window.localStorage.getItem('Fw4_access_Token')
            };

            let empTasks = await axios.get(APIURL + "/employees/EmployeeTasks", { params: { comp_id: props.user.comp_id, employee_id: props.user.emp_id } });
            if (empTasks.data.length !== 0) {
                let empTaskTypes = [];
                empTasks.data.forEach((task) => empTaskTypes.push(task.EntityValue));
                setEmpTasksAvailable(true);
                setTypeList(empTaskTypes);

                if (props.edit) {
                    setBasicDetails(prev => ({ ...prev, tType: props.formVals.tas_type }));
                }
            } else if (!props.edit) {
                let taskTypes = await axios.get(APIURL + '/dropdowns/dropdownType/taskType', { params: { comp_id: props.comp_id, employeeType: props.user.type } });
                setTypeList(taskTypes.data);
            }

            if (props.edit) {
                if (!empTasksAvailable) {
                    let custTasks = await axios.get(APIURL + "/customers/CustomerTasks", { params: { comp_id: props.comp_id, customer_id: props.projList[0]?.cus_id } });
                    let cusTaskTypes = [];
                    if (custTasks.data.length !== 0) {
                        custTasks.data.forEach((task) => cusTaskTypes.push(task.EntityValue));
                        setTypeList(cusTaskTypes);
                        setBasicDetails(prev => ({ ...prev, tType: props.formVals.tas_type }));
                    } else {
                        let taskTypes = await axios.get(APIURL + '/dropdowns/dropdownType/taskType', { params: { comp_id: props.comp_id, employeeType: props.user.type } });
                        setTypeList(taskTypes.data);
                        setBasicDetails(prev => ({ ...prev, tType: props.formVals.tas_type }));
                    }
                }
                handleMapsPin({ id: 'tLoc', value: dateLocation.tLoc });
                handleProjLoc(basicDetails.tProject, 'tProject');
                handleProjChange(props.projList[0]?.cus_id, basicDetails.tProject);

                try {
                    const preferencesResponse = await axios.get(APIURL + "/customers/employeePreference", {
                        params: { customer_id: props.projList[0]?.cus_id }
                    });
                    setCustomerPreferences(preferencesResponse.data.preferences || []);
                } catch (err) {
                    console.error('Error fetching customer preferences in edit mode:', err);
                }
            }
        };
        initialize();
    }, []);

    const addTaskToDB = async (e) => {
        e.preventDefault();
        setIsCreatingTask(true);

        const newState = {
            basicDetails: { tTitle: "", tDesc: "", tProject: "", tPreReq: "", tType: "" },
            dateLocation: { tStrtDate: moment().format(DATETIME_FORMAT), tEndDate: moment().format(DATETIME_FORMAT), tLoc: [] },
            recurrenceDetails: null,
            contactList: [],
            finalDetails: { tAssTo: "", tPriority: "", tIsBillable: true, tSigReq: 0, tComment: "", tContacts: [], tTravelTime: "", tExpenses: "" }
        }
        e.persist()
        const startTime = moment(dateLocation.tStrtDate).format('YYYY-MM-DD') + "T" + moment(dateLocation.tStartTime).format('HH:mm:ss')
        const endTime = moment(dateLocation.tEndDate).format('YYYY-MM-DD') + "T" + moment(dateLocation.tEndTime).format('HH:mm:ss')

        const data = {
            empId: props.user.emp_id,
            empName: props.user.emp_name,
            company_id: props.comp_id,
            tTitle: basicDetails.tTitle,
            tDesc: basicDetails.tDesc,
            tProject: parseInt(basicDetails.tProject),
            tPreReq: parseInt(basicDetails.tPreReq),
            tType: basicDetails.tType,
            tStrtDate: startTime,
            tEndDate: endTime,
            tStartTime: convertTime(startTime, moment.tz.guess(), task_emp_timezone, DATETIME_FORMAT) ?? startTime,
            tEndTime: convertTime(endTime, moment.tz.guess(), task_emp_timezone, DATETIME_FORMAT) ?? endTime,
            tLoc: dateLocation.tLoc[0]?.value || null,
            tLoc1: dateLocation.tLoc[1]?.value || null,
            tLoc2: dateLocation.tLoc[2]?.value || null,
            tOloc: tOloc || null,
            tAssTo: parseInt(finalDetails.tAssTo),
            tPriority: finalDetails.tPriority,
            tIsBillable: finalDetails.tIsBillable,
            tas_required_sig: finalDetails.tSigReq,
            tNotes: finalDetails.tComment,
            tContacts: finalDetails.tContacts,
            tTravelTime: parseTravelTimeMinutes(finalDetails.tTravelTime),
            tExpenses: finalDetails.tExpenses ? parseFloat(finalDetails.tExpenses) : null,
            taskId: props.taskId,
            user_id: props.user.emp_id,
            task_emp_timezone: task_emp_timezone || ''
        };
        let msg = null;

        if (props.edit) {
            msg = await axios.put(APIURL + "/task", data);
        } else {
            if (recurrenceDetails !== null) {
                msg = await axios.post(APIURL + "/task/recurringTask", { ...data, ...recurrenceDetails });
            } else {
                msg = await axios.post(APIURL + "/task", data);
            }
        }
        if (msg.status === 200) {
            let project_customer_id = (await axios.get(APIURL + `/project?proj_id=${basicDetails.tProject}&comp_id=${props.comp_id}`)).data.pro_customer_id

            try {
                if (finalDetails.tComment) {
                    await axios.post(APIURL + `/comments`, {
                        cus_id: project_customer_id,
                        pro_id: parseInt(basicDetails.tProject),
                        tas_id: msg.data.id,
                        emp_id: props.user.emp_id,
                        comment_text: finalDetails.tComment,
                    })
                }
            } catch (err) {
                console.log(err)
            }

            e.target.reset();
            setBasicDetails(newState.basicDetails);
            setDateLocation(newState.dateLocation);
            setFinalDetails(newState.finalDetails);
            setTOloc(null);
            setRecurrenceDetails(newState.recurrenceDetails);
            setShowModal(true);
            setAddError(false);
            setIsCreatingTask(false);
            if (msg.data.tasks) setTaskDates(msg.data.tasks);
        } else {
            setAddError(true);
            setShowModal(true);
            setTaskDates([]);
            setIsCreatingTask(false);
        }
    }

    const handleGoHome = () => {
        setAddError(false);
        setShowModal(false);
        setShowAlert(false);
        setShowNoEmployeeAlert(false);

        if (props.goHome !== undefined) {
            props.goHome()
        } else {
            setGoHome(true);
        }
    }

    const addNew = () => {
        setGoHome(false);
        setBasicDetails({ tTitle: "", tDesc: "", tProject: "", tPreReq: "No Pre Req", tType: "" });
        setDateLocation({
            tStrtDate: moment().format(DATETIME_FORMAT),
            tEndDate: moment().add(1, 'm').format(DATETIME_FORMAT),
            tStartTime: moment().format(DATETIME_FORMAT),
            tEndTime: moment().add(1, 'm').format(DATETIME_FORMAT),
            tLoc: [],
        });
        setRecurrenceDetails(null);
        setFinalDetails({ tAssTo: "", tPriority: "Med", tIsBillable: true, tSigReq: 0, tComment: "", tContacts: [] });
        setMapPushPins([]);
        setTaskDates([]);
        setShowModal(false);
        setAddError(false);
        setShowAlert(false);
        setShowNoEmployeeAlert(false);
        setErrorMsg('');
        setLocList([]);
        setTaskAuthorizationStatus(null);
        setTaskAuthorizationText('');
        setCust_id(null);
        setContactList([]);
        setTaskList([]);
        setIsCreatingTask(false);
        setCustomerPreferences([]);
    }

    const validateAll = () => {
        const sections = [basicDetails, dateLocation, finalDetails];
        for (let section of sections) {
            const validation = props.isFormValidated(section);
            if (!validation.isValidated) {
                return false;
            }
        }

        if (!(moment(dateLocation.tEndDate).isAfter(dateLocation.tStrtDate) && moment(dateLocation.tEndTime).isAfter(dateLocation.tStartTime))) {
            setErrorMsg(`End Date/Time must be after Start Date/Time.`);
            setShowAlert(true);
            return false;
        }

        setShowAlert(false);
        setShowNoEmployeeAlert(false);
        return true;
    }

    const handleProjChange = (customer_id, project_id) => {
        setCust_id(customer_id);

        if (!props.edit && project_id) {
            axios.get(APIURL + '/projemployees', { params: { pro_id: project_id, comp_id: props.comp_id } })
                .then((peaResponse) => {
                    if (!peaResponse.data || peaResponse.data.length === 0) {
                        setErrorMsg('No employees are assigned to the selected project/customer. Please assign employees to this project before creating a task.');
                        setShowNoEmployeeAlert(true);
                    } else {
                        setShowNoEmployeeAlert(false);
                        if (showAlert === false) setErrorMsg('');
                    }
                })
                .catch((err) => console.error('Error checking project employee assignments:', err));
        }

        if (project_id) {
            axios.get(APIURL + '/project/projCont', { params: { proj_id: project_id } })
                .then((res) => {
                    const contacts = res.data.map((contact) => ({
                        id: contact.con_id,
                        label: contact.con_first_name + " " + contact.con_last_name
                    }));
                    setContactList(contacts);
                })
                .catch((err) => console.log(err));
        }

        axios.get(APIURL + '/customers/employeePreference', { params: { customer_id: customer_id } })
            .then((res) => setCustomerPreferences(res.data.preferences || []))
            .catch((err) => console.error('Error fetching customer preferences:', err));
    }

    const handleProjLoc = (id, field) => {
        if (field === 'tProject') {
            axios.get(APIURL + '/location/v2', { params: { company_id: props.comp_id, project_id: id } }).then((res) => {
                let locations = res.data.map((loc) => ({
                    id: loc.loc_id,
                    location_addr1: loc.loc_addr_line1,
                    location_addr2: loc.loc_addr_line2,
                    location_city: loc.loc_city,
                    location_state: loc.loc_state,
                    location_zip: loc.loc_zip,
                    location_lat: loc.loc_lat,
                    location_lon: loc.loc_lon,
                }))
                setBasicDetails(prev => ({ ...prev, [field]: id }));
                setDateLocation(prev => ({ ...prev }));
                setFinalDetails(prev => (!props.edit ? { ...prev, tAssTo: '' } : { ...prev }));
                setMapPushPins([]);
                setLocList(locations || []);
            }).catch((err) => console.log(err))
        } else if (field === 'tPreReq') {
            setBasicDetails(prev => ({ ...prev, [field]: id }));
        } else if (field === 'taskStartTime' || field === 'taskEndTime' || field === 'tStrtDate' || field === 'tEndDate' || field === 'tStartTime' || field === 'tEndTime') {
            const selectedProj = findSelectedProject();
            let newDateLocation = { ...dateLocation }
            if (field === 'tStrtDate') {
                newDateLocation.tStrtDate = moment(id).format(DATETIME_FORMAT)
                newDateLocation.tEndDate = moment(id).format(DATETIME_FORMAT)
                setDateLocation(newDateLocation);
                return
            } else if (field === 'tEndDate') {
                if (selectedProj && moment(id).isBetween(selectedProj.start_date, selectedProj.end_date) && !!newDateLocation.tStrtDate && moment(id).isSameOrAfter(moment(newDateLocation.tStrtDate))) {
                    newDateLocation.tEndDate = moment(id).format(DATETIME_FORMAT)
                } else {
                    newDateLocation.tEndDate = ''
                    setDateLocation(newDateLocation);
                    setErrorMsg(`End Date must be between range ${selectedProj ? moment(selectedProj.start_date).format('DD MMM YYYY') + ' - ' + moment(selectedProj.end_date).format('DD MMM YYYY') : 'project dates'} and be Same or After ${moment().format('DD MMM YYYY')}`);
                    setShowAlert(true);
                    return
                }
            } else if (field === 'tStartTime') {
                let newTime = moment(id).format(DATETIME_FORMAT)
                if (moment(newDateLocation.tStrtDate.substring(0, 10) + 'T' + newTime.substring(11)).isBefore(newDateLocation.tEndDate.substring(0, 10) + 'T' + newDateLocation.tEndTime.substring(11))) {
                    newDateLocation.tStartTime = newTime;
                } else {
                    newDateLocation.tStartTime = ''
                    setErrorMsg(`Start Time must be before ${moment(newDateLocation.tEndTime).format('hh:mm')} `);
                    setShowAlert(true);
                    return;
                }
            } else if (field === 'tEndTime') {
                let newTime = moment(id).format(DATETIME_FORMAT);
                if ((moment(newDateLocation.tStrtDate.substring(0, 10) + 'T' + newDateLocation.tStartTime.substring(11)).isBefore(newDateLocation.tEndDate.substring(0, 10) + 'T' + newTime.substring(11)))) {
                    newDateLocation.tEndTime = newTime;
                } else {
                    setErrorMsg(`End Time must be after range ${moment(newDateLocation.tStartTime).format('HH:mm')} `);
                    setShowAlert(true);
                    return
                }
            } else if (field === 'taskStartTime') {
                newDateLocation.tStrtDate = moment(id).format(DATETIME_FORMAT)
                newDateLocation.tStartTime = moment(id).format(DATETIME_FORMAT)
            } else if (field === 'taskEndTime') {
                newDateLocation.tEndDate = moment(id).format(DATETIME_FORMAT)
                newDateLocation.tEndTime = moment(id).format(DATETIME_FORMAT)
            } else {
                newDateLocation = { ...dateLocation, [field]: moment(id).format(DATETIME_FORMAT) }
            }
            if ((field === 'taskStartTime' || field === 'taskEndTime') &&
                !moment(newDateLocation.tEndDate).isAfter(moment(newDateLocation.tStrtDate))) {
                setErrorMsg('End Date/Time must be after Start Date/Time.');
                setShowAlert(true);
            } else {
                setErrorMsg('');
                setShowAlert(false);
            }
            setShowNoEmployeeAlert(false);
            setDateLocation(newDateLocation);
        } else if (field === 'tAssTo') {
            setFinalDetails(prev => ({ ...prev, [field]: id }));
        } else if (field === 'task_emp_timezone') {
            setTask_emp_timezone(id);
        } else {
            setDateLocation(prev => ({ ...prev, [field]: id }));
            setMapPushPins([]);
        }
    }

    const handleBasicDetsChange = (e) => {
        setBasicDetails(prev => ({ ...prev, [e.target.id]: e.target.value }));
    }

    const buildMapPin = (loc) => {
        const lat = props.edit ? +loc.loc_lat : +loc.location_lat;
        const lon = props.edit ? +loc.loc_lon : +loc.location_lon;
        const city = props.edit ? loc.loc_city : loc.location_city;
        const addr1 = props.edit ? loc.loc_addr_line1 : loc.location_addr1;
        const addr2 = props.edit ? loc.loc_addr_line2 : loc.location_addr2;
        return {
            location: [lat, lon],
            infoboxOption: { title: city, visible: true, description: addr1 + ',' + addr2 },
            pushPinOption: { title: 'Task Location' },
        };
    };

    const handleMapsPin = (e) => {
        setDateLocation(prev => ({ ...prev, [e.id]: e.value }));
        const chosenLocIds = (e.value || []).map((v) => v.value);
        const chosenLocs = locList.filter(({ id }) => chosenLocIds.includes(id));
        const noSelection = e.value === '' || !e.value || e.value.length === 0 || !chosenLocs;
        setMapPushPins(noSelection ? [] : chosenLocs.map(buildMapPin));
    }

    const handleFinalDetsChange = (e) => {
        const id = e.target.id;
        setFinalDetails(prev => {
            if (FINAL_DETS_ACTIONS[id]) return { ...prev, ...FINAL_DETS_ACTIONS[id] };
            if (id === 'tNotes') return { ...prev, tComment: e.target.value };
            return { ...prev, [id]: e.target.value };
        });
    }

    const handleRecurrenceSubmit = (recDets) => {
        recDets.projectEndDate = findSelectedProject()?.end_date;
        setRecurrenceDetails(recDets);
    }

    const handleRecurrenceCancel = () => setRecurrenceDetails(null);

    const getEmpList = () => {
        try {
            let empList = props.edit
                ? props.empList
                : (!basicDetails.tProject ? [] : findSelectedProject()?.empAssn?.map((relEmp) => {
                    if (props.empList.some((emp) => emp.id === relEmp.id)) {
                        return (props.empList || []).find((rEmp) => rEmp && rEmp.id === relEmp.id);
                    }
                    return null;
                })) || [];

            if (customerPreferences?.length > 0 && empList?.length > 0) {
                empList = [...empList].sort((a, b) => {
                    const levelA = customerPreferences.find(p => p.employee_id === a?.id)?.preference_level ?? 0;
                    const levelB = customerPreferences.find(p => p.employee_id === b?.id)?.preference_level ?? 0;
                    return levelB - levelA;
                });
            }
            return empList;
        } catch (err) {
            setErrorMsg('Please ensure that the project being selected has an employee assigned to it!');
            setShowNoEmployeeAlert(true);
            setBasicDetails(prev => ({ ...prev, tProject: '' }));
            return [];
        }
    }

    const setTaskAuthStatus = async () => {
        if (!cust_id || !basicDetails.tType || basicDetails.tType === '') return null;

        const selectedDate = dateLocation.tStartTime;
        let preAuthList = await axios.get(APIURL + '/preAuth/taskType', {
            params: {
                companyID: props.comp_id,
                taskType: basicDetails.tType,
                fromDate: moment(selectedDate).isValid() ? moment(selectedDate).subtract(2, 'months').format('MM/DD/YYYY') : null,
                toDate: moment(selectedDate).isValid() ? moment(selectedDate).endOf("day").format('MM/DD/YYYY') : null
            }
        });

        preAuthList = preAuthList.data.sort((a, b) => new Date(b.startDate) - new Date(a.startDate));
        const preAuth = preAuthList.find(item => item.cus_id === cust_id);
        return preAuth ? Math.max(0, preAuth.remainingUnits) : null;
    }

    return (
        <div style={{ textAlign: 'left' }}>
            {showModal ? (
                <MsgModalBox edit={props.edit} taskDates={taskDates} addNew={addNew} goHome={handleGoHome} text="Task" showError={addError} style={{ position: 'absolute' }} />
            ) : (
                <Box
                    component="form"
                    noValidate
                    onSubmit={(e) => {
                        setSubmitAttempted(true);
                        if (validateAll()) {
                            addTaskToDB(e);
                        } else {
                            e.preventDefault();
                        }
                    }}
                    sx={{ px: 1 }}
                >
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                        <Typography variant="h5" sx={{ fontWeight: 600, color: '#1f3a5f' }}>
                            {props.edit ? 'Edit Task' : (props.customerPage ? '' : 'New Task')}
                        </Typography>
                        {!!taskAuthorizationStatus && (
                            <Tooltip title={taskAuthorizationText} placement="left">
                                <CircleIcon sx={{ color: taskAuthorizationStatus > 0 ? 'green' : taskAuthorizationStatus < 0 ? 'red' : 'yellow' }} />
                            </Tooltip>
                        )}
                    </Box>

                    <Box
                        sx={{
                            display: 'flex',
                            flexDirection: { xs: 'column', lg: 'row' },
                            gap: 2,
                            alignItems: 'stretch',
                        }}
                    >
                        <SectionCard
                            icon={<AssignmentOutlinedIcon sx={{ color: '#1976d2' }} />}
                            title="Basic Details"
                        >
                            <BasicDetsCard
                                edit={props.edit}
                                taskList={taskList}
                                projList={props.edit ? (Array.isArray(props.projList) ? props.projList : []) : (Array.isArray(projectList) ? projectList : [])}
                                taskType={typeList}
                                empList={props.empList}
                                formVals={basicDetails}
                                project_name={props.edit ? (Array.isArray(props.projList) && props.projList[0]?.pname) : ''}
                                pre_task_name={props.edit ? (props.formVals.tas_prequisites_name ? props.formVals.tas_prequisites_name : 'NO TASK PREREQUISITE') : 'NO TASK PREREQUISITE'}
                                handleProjLoc={handleProjLoc}
                                handleProjChange={handleProjChange}
                                handleBasicDetsChange={handleBasicDetsChange}
                                isSupport={isSupport}
                                user={props.user}
                                customerPage={props.customerPage}
                                cus_id={props.cus_id}
                                submitAttempted={submitAttempted}
                                noEmployeeError={showNoEmployeeAlert ? (errorMsg || 'Please ensure that the project being selected has an employee assigned to it!') : ''}
                            />
                        </SectionCard>

                        <SectionCard
                            icon={<EventOutlinedIcon sx={{ color: '#1976d2' }} />}
                            title="Date & Location"
                        >
                            <DateLocationCard
                                edit={props.edit}
                                token={props.token}
                                mapRender={props.mapRender}
                                locList={locList}
                                officeLocList={officeLocList}
                                tOloc={tOloc}
                                onOfficeLocChange={setTOloc}
                                currentPin={loc_comp_pos}
                                task_location={props.edit ? props.task_location[0] : ''}
                                timeAlloc={[dateLocation.tStartTime, dateLocation.tEndTime]}
                                timeIndicator={[dateLocation.timeStrtIndicator, dateLocation.timeEndIndicator]}
                                project={(props.edit ? (Array.isArray(props.projList) ? props.projList : []) : (Array.isArray(projectList) ? projectList : [])).find((proj) => proj && proj.pid === +basicDetails.tProject)}
                                formVals={dateLocation}
                                handleMapsPin={handleMapsPin}
                                mapsPushPin={mapPushPins}
                                error={errorMsg}
                                handleProjLoc={handleProjLoc}
                                updateMapRender={props.updateMapRender}
                                handleRecurrenceSubmit={handleRecurrenceSubmit}
                                handleRecurrenceCancel={handleRecurrenceCancel}
                                submitAttempted={submitAttempted}
                                dateError={showAlert && /date|time/i.test(errorMsg || '') ? errorMsg : ''}
                            />
                        </SectionCard>

                        <SectionCard
                            icon={<TuneOutlinedIcon sx={{ color: '#1976d2' }} />}
                            title="Final Details"
                        >
                            <FinalDetsCard
                                edit={props.edit}
                                handleProjLoc={handleProjLoc}
                                handleFinalDetsChange={handleFinalDetsChange}
                                formVals={finalDetails}
                                empList={getEmpList()}
                                contactList={contactList}
                                user={props.user}
                                submitAttempted={submitAttempted}
                            />
                        </SectionCard>
                    </Box>

                    <Box sx={{ display: 'flex', justifyContent: 'center', mt: 1 }}>
                        <Button
                            type="submit"
                            variant="contained"
                            size='large'
                            disabled={isCreatingTask}
                            startIcon={isCreatingTask ? <CircularProgress size={16} color="inherit" /> : null}
                        >
                            {isCreatingTask
                                ? (props.edit ? 'Saving Task...' : 'Creating Task(s)...')
                                : (props.edit ? 'Save Task' : 'Create Task')}
                        </Button>
                    </Box>
                </Box>
            )}
            {goHome && <Redirect to="/" />}
        </div>
    );
};

export default TaskForm_v2;
